<template>
  <div class="action-dropdown" ref="root" v-click-outside="handleOutsideClick">
    <div
      ref="trigger"
      class="action-dropdown__trigger"
      :class="{ 'is-disabled': disabled }"
      role="button"
      tabindex="0"
      @click.stop="toggle"
      @keydown.enter.prevent="toggle"
      @keydown.space.prevent="toggle"
      @keydown.down.prevent="openAndFocus"
    >
      <slot name="trigger"></slot>
    </div>

    <transition name="action-menu-fade">
      <div
        v-if="open"
        ref="menu"
        class="action-dropdown__menu"
        :class="{ 'is-up': openUp }"
        :style="menuStyle"
        role="menu"
        @mousedown.stop
      >
        <div v-if="title" class="action-dropdown__title">{{ title }}</div>
        <ul class="action-dropdown__list">
          <li
            v-for="(item, index) in items"
            :key="item.id || item.label || index"
            class="action-dropdown__item"
            :class="{
              'is-disabled': item.disabled,
              'is-highlighted': index === highlightedIndex
            }"
            role="menuitem"
            @mouseenter="setHighlight(index)"
            @mouseleave="setHighlight(-1)"
            @click.stop="select(item)"
          >
            <img v-if="item.icon" class="action-dropdown__icon" :src="item.icon" alt="" />
            <span class="action-dropdown__label">{{ item.label }}</span>
          </li>
        </ul>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'ActionDropdown',
  props: {
    items: {
      type: Array,
      default: () => []
    },
    title: {
      type: String,
      default: ''
    },
    minWidth: {
      type: Number,
      default: 200
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      instanceId: null,
      open: false,
      openUp: false,
      menuStyle: {},
      highlightedIndex: -1
    }
  },
  methods: {
    toggle () {
      if (this.disabled || this.items.length === 0) {
        return
      }
      this.open = !this.open
      if (this.open) {
        window.dispatchEvent(new CustomEvent('action-dropdown-open', { detail: this.instanceId }))
        this.initHighlight()
        this.addKeyListener()
        this.$nextTick(() => {
          this.updatePosition()
        })
      } else {
        this.cleanup()
      }
    },
    openAndFocus () {
      if (!this.open) {
        this.toggle()
      }
    },
    close () {
      if (!this.open) return
      this.open = false
      this.cleanup()
    },
    cleanup () {
      this.removeKeyListener()
      this.menuStyle = {}
      this.highlightedIndex = -1
    },
    initHighlight () {
      const firstEnabled = this.items.findIndex(item => !item.disabled)
      this.highlightedIndex = firstEnabled
    },
    setHighlight (index) {
      if (index < 0) {
        this.highlightedIndex = -1
        return
      }
      if (this.items[index] && !this.items[index].disabled) {
        this.highlightedIndex = index
      }
    },
    select (item) {
      if (item.disabled) return
      this.$emit('select', item)
      this.close()
    },
    updatePosition () {
      const triggerEl = this.$refs.trigger
      const menuEl = this.$refs.menu
      if (!triggerEl || !menuEl) return

      const triggerRect = triggerEl.getBoundingClientRect()
      const menuRect = menuEl.getBoundingClientRect()
      const spaceBelow = window.innerHeight - triggerRect.bottom
      const spaceAbove = triggerRect.top

      const menuWidth = Math.max(this.minWidth, Math.ceil(menuRect.width), Math.ceil(triggerRect.width))
      let left = triggerRect.left - (menuWidth - triggerRect.width) / 2

      if (left + menuWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - menuWidth - 8)
      }
      if (left < 8) {
        left = 8
      }
      if (spaceBelow < menuRect.height && spaceAbove >= menuRect.height) {
        this.openUp = true
        this.menuStyle = {
          position: 'fixed',
          left: `${left}px`,
          top: `${Math.max(8, triggerRect.top - menuRect.height - 6)}px`,
          width: `${menuWidth}px`,
          zIndex: 3
        }
      } else {
        this.openUp = false
        this.menuStyle = {
          position: 'fixed',
          left: `${left}px`,
          top: `${triggerRect.top + 30}px`,
          width: `${menuWidth}px`,
          zIndex: 3
        }
      }
    },
    addKeyListener () {
      window.addEventListener('keydown', this.handleKeyDown)
      window.addEventListener('scroll', this.handleViewportChange, true)
      window.addEventListener('resize', this.handleViewportChange)
      window.addEventListener('click', this.handleOutsideClick)
    },
    removeKeyListener () {
      window.removeEventListener('keydown', this.handleKeyDown)
      window.removeEventListener('scroll', this.handleViewportChange, true)
      window.removeEventListener('resize', this.handleViewportChange)
      window.removeEventListener('click', this.handleOutsideClick)
    },
    handleViewportChange () {
      if (this.open) {
        this.updatePosition()
      }
    },
    handleOutsideClick (event) {
      if (!this.open) return
      if (!this.$refs.root.contains(event.target)) {
        this.close()
      }
    },
    handleOtherDropdownOpen (event) {
      if (!this.open) return
      if (event.detail !== this.instanceId) {
        this.close()
      }
    },
    moveHighlight (direction) {
      if (!this.items.length) return
      let nextIndex = this.highlightedIndex
      const max = this.items.length - 1
      for (let i = 0; i <= max; i++) {
        nextIndex = direction === 'up'
          ? (nextIndex <= 0 ? max : nextIndex - 1)
          : (nextIndex >= max ? 0 : nextIndex + 1)
        if (!this.items[nextIndex].disabled) {
          this.highlightedIndex = nextIndex
          this.scrollHighlightedIntoView()
          return
        }
      }
    },
    handleKeyDown (e) {
      if (!this.open) return
      switch (e.key) {
      case 'ArrowUp':
        e.preventDefault()
        this.moveHighlight('up')
        break
      case 'ArrowDown':
        e.preventDefault()
        this.moveHighlight('down')
        break
      case 'Enter':
        e.preventDefault()
        if (this.highlightedIndex >= 0) {
          this.select(this.items[this.highlightedIndex])
        }
        break
      case 'Escape':
        e.preventDefault()
        this.close()
        break
      }
    },
    scrollHighlightedIntoView () {
      this.$nextTick(() => {
        const menuEl = this.$refs.menu
        if (!menuEl) return
        const items = menuEl.querySelectorAll('.action-dropdown__item')
        const el = items[this.highlightedIndex]
        if (el && el.scrollIntoView) {
          el.scrollIntoView({ block: 'nearest' })
        }
      })
    }
  },
  mounted () {
    this.instanceId = `action-dd-${Math.random().toString(36).slice(2)}`
    window.addEventListener('action-dropdown-open', this.handleOtherDropdownOpen)
  },
  beforeDestroy () {
    window.removeEventListener('action-dropdown-open', this.handleOtherDropdownOpen)
    this.removeKeyListener()
  }
}
</script>

<style lang="scss" scoped>
.action-dropdown {
  display: inline-flex;
  position: relative;
}

.action-dropdown__trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &.is-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

.action-dropdown__menu {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.12);
  overflow: hidden;
}

.action-dropdown__title {
  padding: 10px 12px;
  font-size: var(--text-xs1);
  font-weight: 700;
  color: #64748b;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  border-bottom: 1px solid #eef2f6;
}

.action-dropdown__list {
  list-style: none;
  margin: 0;
  padding: 6px;
  max-height: 240px;
  overflow: auto;
}

.action-dropdown__item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  color: #0f172a;
  font-size: var(--text-xs1);
  cursor: pointer;

  &.is-disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &.is-highlighted,
  &:hover {
    background: #f1f5ff;
    color: #1d4ed8;
  }
}

.action-dropdown__icon {
  width: 16px;
  height: 16px;
}

.action-menu-fade-enter-active,
.action-menu-fade-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.action-menu-fade-enter,
.action-menu-fade-leave-to {
  opacity: 0;
  transform: translateY(4px);
}
</style>
